/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import { useSelector } from "react-redux";
import styled from "styled-components";

import { BasePlate } from "src/classes/plate/BasePlate/BasePlate";
import { Locked as LockedIcon } from "src/components/UI/Icons";
import { AppState } from "src/store";

import { SourceIcon } from "./SourceIcon";

export const COMPUTED_SCREENSHOT_ASPECT_RATIO = 120 / 205.41; // from looking at how the images are actually rendered

interface Props {
  plate: BasePlate;
  isSourcePage?: boolean;
  useBigImage?: boolean;
  handleClick?: () => void;
  styles?: React.CSSProperties;
  locked?: boolean;
}

export const PageScreenshot = ({ plate, handleClick, styles, locked, isSourcePage, useBigImage }: Props) => {
  const theme = useSelector((state: AppState) => state.globalUI.uiSettings.theme);
  const isPublic = plate.observe((state) => state.isPublic);
  return (
    <PageScreenshotWrapper>
      {!locked ? (
        <StyledScreenshotImg
          isPublic={isPublic}
          onClick={handleClick}
          style={styles}
          plateTitle={plate.snapshot.title}
          key={plate.snapshot.screenshotTime?.toString() || plate.snapshot.id}
          src={plate.getThumbnailUrl({ size: useBigImage ? "big" : "small", theme })}
          draggable={false}
        />
      ) : (
        <StyledLockedPage>
          <LockedIcon />
        </StyledLockedPage>
      )}
      {isSourcePage && <SourceIcon />}
    </PageScreenshotWrapper>
  );
};

export const PageScreenshotWrapper = styled.div`
  position: relative;
  &:hover {
    color: ${({ theme }) => theme.text.subtle};
  }
`;

interface StyledScreenshotImgProps {
  isPublic: boolean;
  plateTitle: string;
}

export const StyledScreenshotImg = styled.img.attrs((props: StyledScreenshotImgProps) => ({
  "data-testid": `page-screenshot-${props.plateTitle}`,
}))<StyledScreenshotImgProps>`
  width: 100%;
  aspect-ratio: ${COMPUTED_SCREENSHOT_ASPECT_RATIO}; // ensures the image is rendered the same size even before load completes
  object-fit: contain;
  position: relative;
  border: ${({ theme, isPublic }) => (isPublic ? `2px solid ${theme.primary}` : `2px solid ${theme.accent}`)};
  transition-duration: all 0.2s ease;
  background-color: ${({ theme }) => theme.background};
  &:hover {
    border-color: ${({ theme }) => theme.text.subtle};
  }
  &:active {
    border-color: ${({ theme }) => theme.block.border};
  }
`;

const StyledLockedPage = styled.div`
  cursor: default;
  width: 150px;
  aspect-ratio: ${COMPUTED_SCREENSHOT_ASPECT_RATIO};
  position: relative;
  border: ${({ theme }) => `2px solid ${theme.accent}`};
  color: ${({ theme }) => theme.accent};
  background-color: ${({ theme }) => theme.background};
  display: flex;
  align-items: center;
  justify-content: center;
`;
